import { apiService, ApiResponse } from './apiService'
import type { SearchOptions } from '../types/api.types'

/**
 * Generic Table Service
 * Handles /search endpoint pattern for all tables
 *
 * This is a pure HTTP layer with no business logic.
 * Business services (pipelineService, blobService, dataService) use this
 * to perform search operations and add their own transformation logic.
 */
export class TableService {
  /**
   * Execute a search query against any table endpoint
   *
   * @param tablePath - Base path to table endpoint (e.g., '/pipelines_data', '/offerings')
   * @param options - Search options (filters, sorting, pagination)
   * @returns API response with items and total count
   *
   * @example
   * // Search pipelines
   * const response = await tableService.search('/pipelines_data', {
   *   filters: [{ key: 'targetTable', constraint_type: 'EQUAL', value: 'offerings' }],
   *   order_by: [{ key: 'createdAt', direction: 'desc' }],
   *   limit: 50
   * })
   *
   * @example
   * // Search offerings
   * const response = await tableService.search('/offerings', {
   *   filters: [{ key: 'deletedAt', constraint_type: 'IS_NULL' }]
   * })
   */
  async search<T>(
    tablePath: string,
    options?: SearchOptions
  ): Promise<ApiResponse<T[]>> {
    // Normalize table path (ensure leading slash, no trailing slash)
    const normalizedPath = tablePath.startsWith('/')
      ? tablePath
      : `/${tablePath}`
    const cleanPath = normalizedPath.replace(/\/$/, '')

    // Build endpoint with /search suffix and include_total query param
    const endpoint = `${cleanPath}/search?include_total=true`

    console.log('🔍 Table search:', {
      tablePath: cleanPath,
      endpoint,
      options,
    })

    // Execute POST request with search options in body
    // Backend now returns: { data: [...], meta: { total, limit, offset, returned, hasMore } }
    const response = await apiService.post<T[]>(endpoint, options || {})

    if (!response.success) {
      return response as unknown as ApiResponse<T[]>
    }

    // Data is now directly in response.data (array), metadata in response.meta
    // Fallback to options values if server doesn't provide them
    return {
      success: true,
      data: response.data || [],
      message: response.message,
      meta: {
        total: response.meta?.total,
        offset: response.meta?.offset ?? options?.offset,
        limit: response.meta?.limit ?? options?.limit,
        returned: response.meta?.returned,
        hasMore: response.meta?.hasMore,
        ...response.meta,
      },
    } as unknown as ApiResponse<T[]>
  }

  /**
   * Execute a search and return just the items (without total count)
   * Useful when you don't need pagination metadata
   */
  async searchItems<T>(
    tablePath: string,
    options?: SearchOptions
  ): Promise<T[]> {
    const response = await this.search<T>(tablePath, options)
    return response.data || []
  }

  /**
   * Get total count of matching records without fetching items
   * Useful for pagination UI
   */
  async count(tablePath: string, filters?: SearchOptions['filters']): Promise<number> {
    const response = await this.search(tablePath, {
      filters,
      limit: 0, // Don't fetch items, just the count
    })
    return response.meta?.total || 0
  }
}

// Export singleton instance
export const tableService = new TableService()
export default tableService
